import React, { useEffect, useState, createContext, useContext, useCallback, useRef } from "react";

import { Auth } from "aws-amplify";
import {
  AuthenticationDetails,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoAccessToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";

import { AppEvent, triggerAppEvent, useAppContext } from "./app.context";
import {
  VerificationCodeRequest,
  LoginRequest,
  LoginResult,
  RegisterRequest,
  VerifyResetRequest,
  VerifyRegisterRequest,
  ChangePasswordRequest,
  CompleteNewPasswordRequest,
} from "../models/login-request.model";
import errorMessages from "../translations/en/errors.json";
import { AuthSession } from "../models/auth-session.model";
import { useLocation, useHistory } from "react-router-dom";
import { useQueryClient } from "react-query";
import queryString from "query-string";
import axios from "axios";
import environment from "../env";
import { SsoExchangeResponse } from "../models/sso.model";
import {
  AuthError,
  describeOAuthCallbackError,
  describeRefreshError,
  describeSsoExchangeError,
  isAuthError,
  SSO_EXCHANGE_FAILED_ERROR,
  SSO_STATE_MISMATCH_ERROR,
  SSO_UNSUPPORTED_CALLBACK_ERROR,
  withServerErrorMessage,
} from "../shared/utils/auth-error";

// Types

export interface AuthContext {
  session?: AuthSession | null;
  setSession(session: (AuthSession & { username: string }) | null): void;
  /**
   * Resolves with null once signed in, or with a challenge the caller must complete first --
   * today only NEW_PASSWORD_REQUIRED, for accounts whose password was reset by a Super Admin.
   */
  login(request: LoginRequest): Promise<LoginResult>;
  completeNewPassword(request: CompleteNewPasswordRequest): Promise<void>;
  register(request: RegisterRequest): Promise<void>;
  refresh(): Promise<AuthSession>;
  verifySignUp(request: VerifyRegisterRequest): Promise<void>;
  resendSignUpCode(request: VerificationCodeRequest): Promise<void>;
  resetPassword(request: VerificationCodeRequest): Promise<void>;
  verifyResetPassword(request: VerifyResetRequest): Promise<void>;
  changePassword(request: ChangePasswordRequest): Promise<void>;
  signOut(reason?: AuthError | null): Promise<void>;
  isAuthReady: boolean;
  /**
   * Why the user is looking at the login page rather than the app -- an SSO callback failure,
   * an expired session, a forced sign out. Null when they simply aren't signed in yet.
   */
  authError: AuthError | null;
  clearAuthError(): void;
}

const Context = createContext<AuthContext>(null!);

const { defaultClientId, idpClientId, userPoolId, domain: cognitoDomain } = environment.cognito;

// SSO state helpers

interface SsoStatePayload {
  csrf: string;
  providerName: string;
  email: string;
  remember?: boolean;
}

/**
 * Decodes a base64-encoded state string produced by LoginForm during app-initiated SSO.
 * Returns null if the string is missing, malformed, or not our format.
 */
function parseSsoState(state: string | undefined | null): SsoStatePayload | null {
  if (!state) return null;
  try {
    const decoded = JSON.parse(atob(state));
    if (
      typeof decoded?.csrf !== "string" ||
      typeof decoded?.providerName !== "string" ||
      typeof decoded?.email !== "string"
    ) return null;
    return decoded as SsoStatePayload;
  } catch {
    return null;
  }
}

// Session-continuity helpers
//
// Two persistence modes, chosen at login time via the "Stay logged in" checkbox:
//  - remember=false (default): the session lives only in this tab's sessionStorage, exactly
//    like the app's original behavior -- closing the tab (or opening a new one) loses it.
//  - remember=true: the backend also sets an HttpOnly refresh-token cookie (browser-instance
//    scoped, not tab-scoped) on login/refresh/SSO exchange, plus a non-HttpOnly marker cookie
//    carrying no sensitive data. That's what lets a brand new tab -- e.g. one opened from an
//    email notification link -- pick up an existing login without a re-login prompt, while
//    still being cleared the moment the browser process exits.
//
// Presence of a sessionStorage entry is used as the signal for which mode a given tab is in;
// presence of the marker cookie is used to avoid firing a silent-refresh network call when
// there's clearly no cookie-based session to restore (e.g. a fresh visit to /login).

const SESSION_STORAGE_KEY = "context/session";
const HAS_SESSION_COOKIE = "zapit_has_session";

type StoredSession = AuthSession & { username: string };

function readStoredSession(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredSession) : null;
  } catch {
    return null;
  }
}

function writeStoredSession(session: StoredSession | null): void {
  if (session) {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  } else {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  }
}

function hasSessionCookie(): boolean {
  return document.cookie.split("; ").some((entry) => entry.startsWith(`${HAS_SESSION_COOKIE}=`));
}

const authChannel: BroadcastChannel | null = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("zapit-auth") : null;

/** Query values arrive as string | string[] | null depending on how the provider built the URL. */
function firstValue(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  if (Array.isArray(value) && typeof value[0] === "string") return value[0];
  return undefined;
}

// Provider

function AuthContextProvider(props: React.PropsWithChildren<{}>): JSX.Element {
  const history = useHistory();
  const { search } = useLocation();
  const queryClient = useQueryClient();

  // error/error_description are the OAuth2 failure response the provider redirects back with
  // when it refuses the sign in (RFC 6749 4.1.2.1) -- there is no code in that case.
  const [authParams] = useState<{ code?: string; state?: string; error?: string; error_description?: string }>(
    queryString.parse(search) as any
  );

  const [isAuthReady, setIsAuthReady] = useState(false);

  // Set whenever the app drops the user back on the login page for a reason they could not have
  // seen happen -- an SSO callback failure, a dead session, a forced sign out.
  const [authError, setAuthError] = useState<AuthError | null>(null);
  const clearAuthError = useCallback(() => setAuthError(null), []);

  // In-memory cache of the current session. Persistence (if any) happens separately via
  // writeStoredSession (sessionStorage, "remember" off) or the backend's HttpOnly cookie
  // ("remember" on) -- see the Session-continuity helpers above.
  const [session, setSession] = useState<(AuthSession & { username: string }) | null>(null);

  const sessionRef = useRef(session || null);
  const setSessionRef = useRef(setSession);
  useEffect(() => {
    setSessionRef.current = setSession;
  }, [setSession]);

  useEffect(() => {
    setIsAuthReady(false);

    // Configure Amplify (still needed for register, signOut, reset, etc.)
    const userClientType = sessionRef.current?.clientType ?? "app";
    // Any OrgSsoConfig-registered provider (Azure, Google, ...) carries its own Cognito App
    // Client ID on the session -- prefer that. Fall back to the legacy env-level idpClientId
    // only for the IdP-initiated flow below, which bypasses our BE and never gets appClientId.
    const ssoAppClientId = sessionRef.current?.appClientId ?? (userClientType === "microsoftIdp" ? idpClientId : undefined);
    Auth.configure({
      Analytics: { disabled: true },
      Auth: {
        oauth: {
          scope: ["email"],
          responseType: "code",
        },
        region: "us-east-1",
        userPoolId,
        userPoolWebClientId: ssoAppClientId || defaultClientId,
        storage: window.sessionStorage,
        authenticationFlowType: "USER_PASSWORD_AUTH",
      },
    });

    const hasAuthCode = Boolean(authParams?.code);
    const oauthError = firstValue(authParams?.error);

    if (oauthError) {
      const failedState = parseSsoState(firstValue(authParams.state));
      // "error" is too common a query param to own outright: this branch abandons session
      // restore, so a deep link that merely happened to carry ?error= would sign an
      // authenticated user out of the app. Only claim it with evidence it really is a provider
      // callback -- our own state echoed back alongside it, or (IdP-initiated logins send no
      // state) a browser with no session here to disturb in the first place.
      const isProviderCallback = Boolean(failedState) || (readStoredSession() == null && !hasSessionCookie());

      if (isProviderCallback) {
        // The provider refused the sign in and redirected back with an error instead of a code
        // (user cancelled, account not assigned to the app, IdP/Cognito misconfiguration, ...).
        // Without this the callback looks identical to "no session" and the user lands on the
        // login page with no idea why.
        sessionStorage.removeItem("sso_csrf");
        console.error(`[auth] SSO callback error: ${oauthError}`, authParams.error_description);
        setAuthError({
          ...describeOAuthCallbackError(oauthError, firstValue(authParams.error_description)),
          email: failedState?.email,
        });
        history.replace("/login");
        setIsAuthReady(true);
        return;
      }

      // Not ours -- fall through to the normal restore path and leave the URL untouched.
      console.warn(`[auth] ignoring unrelated "error" query param: ${oauthError}`);
    }

    if (hasAuthCode) {
      history.replace("");

      const ssoState = parseSsoState(authParams.state);

      if (ssoState) {
        // App-initiated SSO: validate CSRF then exchange code via the BE.
        const storedCsrf = sessionStorage.getItem("sso_csrf");
        sessionStorage.removeItem("sso_csrf");

        if (storedCsrf && ssoState.csrf !== storedCsrf) {
          // CSRF mismatch -- possible CSRF attack or stale tab; go back to login. The message
          // stays deliberately vague about the cause, but the user is told to try again.
          console.error("[auth] SSO state mismatch -- rejecting callback.");
          setAuthError({ ...SSO_STATE_MISMATCH_ERROR, email: ssoState.email });
          history.replace("/login");
          setIsAuthReady(true);
          return;
        }

        axios
          .post<SsoExchangeResponse>(
            "auth/sso/exchange",
            { code: authParams.code, providerName: ssoState.providerName, email: ssoState.email, remember: ssoState.remember },
            { baseURL: environment.newApiUrl, withCredentials: true }
          )
          .then((response) => {
            const { username, accessToken, issuedAt, expiresIn, refreshToken, appClientId } = response.data;
            // clientType is "sso" for every provider -- appClientId (not clientType) is what
            // identifies the specific Cognito App Client to refresh/reconfigure Amplify against.
            const newSession: StoredSession = { username, clientType: "sso", appClientId, accessToken, issuedAt, expiresIn, refreshToken };
            writeStoredSession(ssoState.remember ? null : newSession);
            setAuthError(null);
            setSessionRef.current(newSession);
          })
          .catch((error) => {
            console.error("[auth] SSO exchange failed:", error);
            setAuthError({ ...describeSsoExchangeError(error), email: ssoState.email });
            history.replace("/login");
          })
          .finally(() => setIsAuthReady(true));
      } else if (idpClientId) {
        // IdP-initiated SSO (Azure portal tile click): exchange code directly with Cognito.
        // Preserved for backward compatibility -- no state means this came from the IdP, not our login page.
        const urlencoded = new URLSearchParams();
        urlencoded.append("grant_type", "authorization_code");
        urlencoded.append("client_id", idpClientId);
        urlencoded.append("code", authParams.code!);
        urlencoded.append("redirect_uri", window.location.origin);

        fetch(`${cognitoDomain}/oauth2/token`, { method: "POST", body: urlencoded, redirect: "follow" })
          .then(async (response) => {
            // fetch only rejects on network failure -- a refused exchange comes back as a
            // non-2xx body carrying the OAuth2 error, so translate that into a real rejection.
            const body = await response.json().catch(() => null);
            if (!response.ok || !body) {
              return Promise.reject(describeOAuthCallbackError(body?.error ?? "server_error", body?.error_description));
            }
            return body;
          })
          .then((result) => {
            if (!result.id_token || !result.access_token || !result.refresh_token) {
              return Promise.reject(new Error("Invalid token response: missing required fields"));
            }
            const localSession = new CognitoUserSession({
              IdToken: new CognitoIdToken({ IdToken: result.id_token }),
              RefreshToken: new CognitoRefreshToken({ RefreshToken: result.refresh_token }),
              AccessToken: new CognitoAccessToken({ AccessToken: result.access_token }),
            });
            // No checkbox context on this path (it bypasses our login page entirely) --
            // always tab-scoped via sessionStorage, matching this app's original behavior.
            const newSession: StoredSession = {
              username: localSession.getAccessToken().payload.username,
              clientType: "microsoftIdp",
              accessToken: localSession.getIdToken().getJwtToken(),
              issuedAt: localSession.getIdToken().getIssuedAt(),
              expiresIn: localSession.getIdToken().getExpiration(),
              refreshToken: localSession.getRefreshToken().getToken(),
            };
            writeStoredSession(newSession);
            setAuthError(null);
            setSessionRef.current(newSession);
          })
          .catch((error) => {
            console.error("[auth] IdP-initiated exchange failed:", error);
            setAuthError(isAuthError(error) ? error : SSO_EXCHANGE_FAILED_ERROR);
          })
          .finally(() => setIsAuthReady(true));
      } else {
        // Code present but no IdP configured and no app-initiated state -- discard. Nothing the
        // user can fix themselves, so point them at their administrator rather than failing mute.
        console.error("[auth] SSO callback received with no usable state and no IdP client configured.");
        setAuthError(SSO_UNSUPPORTED_CALLBACK_ERROR);
        history.replace("/login");
        setIsAuthReady(true);
      }
    } else {
      // No auth code -- try to restore an existing session.
      const stored = readStoredSession();

      if (stored) {
        // "remember" was off at login: tab-scoped sessionStorage entry, restored instantly --
        // exactly this app's original behavior, no network round trip needed.
        setSessionRef.current(stored);
        setIsAuthReady(true);
        return;
      }

      // No sessionStorage entry -- either this is a brand new tab (possibly one opened from
      // an email link) or there's no session at all. Two independent restore paths run
      // concurrently so neither adds latency to the other:
      //  1. Our own HttpOnly refresh-token cookie ("remember" was on at login). This is
      //     browser-instance scoped (not tab-scoped), so it's what lets a brand new tab pick
      //     up an existing login without a re-login prompt.
      //  2. Amplify's own Cognito session storage, kept for backward compatibility with flows
      //     that go through Amplify directly (register/reset/confirm, IdP-initiated SSO).
      const silentRefresh = hasSessionCookie()
        ? axios
            .post<Omit<AuthSession, "clientType"> & { clientType?: string }>(
              "auth/refresh",
              {},
              { baseURL: environment.newApiUrl, withCredentials: true }
            )
            .then((response) => response.data)
            .catch(() => null)
        : Promise.resolve(null);

      const amplifyRestore = Auth.currentSession().catch(() => null);

      Promise.all([silentRefresh, amplifyRestore])
        .then(([refreshed, amplifySession]) => {
          if (refreshed) {
            setSessionRef.current({
              username: refreshed.username ?? sessionRef.current?.username ?? "",
              clientType: refreshed.clientType ?? sessionRef.current?.clientType ?? "app",
              appClientId: refreshed.appClientId ?? sessionRef.current?.appClientId,
              accessToken: refreshed.accessToken,
              issuedAt: refreshed.issuedAt,
              expiresIn: refreshed.expiresIn,
              refreshToken: refreshed.refreshToken,
            });
          } else if (amplifySession) {
            const accessToken = amplifySession.getIdToken().getJwtToken();
            const username = amplifySession.getAccessToken().payload.username;
            setSessionRef.current((prev) => {
              const clientType = sessionRef.current?.clientType ?? "app";
              if (prev?.accessToken === accessToken) return { ...prev, clientType };
              return {
                username,
                clientType,
                accessToken,
                expiresIn: amplifySession.getIdToken().getExpiration(),
                issuedAt: amplifySession.getIdToken().getIssuedAt(),
                refreshToken: amplifySession.getRefreshToken().getToken(),
              };
            });
          }
        })
        .finally(() => setIsAuthReady(true));
    }
  }, [authParams, history]);

  useEffect(() => {
    sessionRef.current = session || null;
  }, [session]);

  // Auth operations

  /**
   * Password login -- calls the BE, which calls Cognito.
   * The BE also enforces SSO-required rules before invoking Cognito.
   */
  const login = useCallback(
    async ({ username: rawUsername, password, remember }: LoginRequest): Promise<LoginResult> => {
      // Send the trimmed username as-typed, NOT lowercased -- the backend's case-sensitivity
      // migration needs the exact case the user typed to fall back to a historical mixed-case
      // Cognito account when no lowercase account exists yet (zapit-api AuthService.login's
      // tiered lookup). Pre-lowercasing here would silently hide that case from the backend and
      // break sign-in for anyone not yet migrated.
      const typedUsername = rawUsername.trim();
      // The session/cookie always store the canonical lowercase form, regardless of which tier
      // the backend actually authenticated against.
      const username = typedUsername.toLowerCase();
      const response = await axios.post<Omit<AuthSession, "username" | "clientType"> & { challenge?: string; session?: string }>(
        "auth/login",
        { username: typedUsername, password, remember },
        { baseURL: environment.newApiUrl, withCredentials: true }
      );
      if (response.data.challenge === "NEW_PASSWORD_REQUIRED" && response.data.session) {
        // Nothing is authenticated yet -- no session is stored and no cookie was set. The caller
        // has to collect a new password and call completeNewPassword.
        return { challenge: "NEW_PASSWORD_REQUIRED", username: typedUsername, session: response.data.session };
      }
      const newSession: StoredSession = {
        username,
        clientType: "app",
        accessToken: response.data.accessToken,
        issuedAt: response.data.issuedAt,
        expiresIn: response.data.expiresIn,
        refreshToken: response.data.refreshToken,
      };
      writeStoredSession(remember ? null : newSession);
      setAuthError(null);
      setSession(newSession);
      return null;
    },
    [setSession]
  );

  /**
   * Completes the NEW_PASSWORD_REQUIRED challenge returned by `login` and signs the user in with
   * the password they just chose -- the backend sets the password and issues the session in one
   * call, so there is no second sign in to fail halfway through.
   */
  const completeNewPassword = useCallback(
    async ({ username: rawUsername, session, newPassword, remember }: CompleteNewPasswordRequest) => {
      const exactUsername = rawUsername.trim();
      const response = await axios.post<Omit<AuthSession, "username" | "clientType">>(
        "auth/challenge/new-password",
        { username: exactUsername, session, newPassword, remember },
        { baseURL: environment.newApiUrl, withCredentials: true }
      );
      const username = exactUsername.toLowerCase();
      const newSession: StoredSession = {
        username,
        clientType: "app",
        accessToken: response.data.accessToken,
        issuedAt: response.data.issuedAt,
        expiresIn: response.data.expiresIn,
        refreshToken: response.data.refreshToken,
      };
      writeStoredSession(remember ? null : newSession);
      setAuthError(null);
      setSession(newSession);
    },
    [setSession]
  );

  const register = useCallback(({ username, password, firstName, lastName }: RegisterRequest) => {
    return axios
      .post<void>(
        "v1/auth/register",
        { username: username.toLowerCase().trim(), password, firstName, lastName },
        { baseURL: environment.newApiUrl }
      )
      .then(() => Promise.resolve())
      .catch(withServerErrorMessage);
  }, []);

  const refresh = useCallback((): Promise<AuthSession> => {
    if (!sessionRef.current) {
      return Promise.reject(errorMessages.auth.unauthorized);
    }
    const { username, refreshToken, clientType, appClientId } = sessionRef.current;
    const userClientType = clientType ?? "app";
    // Presence of a stored entry is this tab's signal for "remember" mode -- mirrors the
    // backend only re-issuing the cookie when the incoming request was itself cookie-sourced.
    const wasStoredLocally = readStoredSession() != null;
    return axios
      .post<AuthSession>(
        "auth/refresh",
        { username, refreshToken, clientType: userClientType, appClientId },
        { baseURL: environment.newApiUrl, withCredentials: true }
      )
      .then((response) => {
        const newSession: StoredSession = { ...response.data, username, clientType: userClientType };
        if (wasStoredLocally) writeStoredSession(newSession);
        setSession(newSession);
        return response.data;
      })
      .catch((error) => {
        // The session is being dropped underneath the user (idle tab, revoked/expired refresh
        // token, unreachable server). They get bounced to the login page, so say why rather than
        // looking like a random logout.
        if (wasStoredLocally) writeStoredSession(null);
        setAuthError(describeRefreshError(error));
        setSession(null);
        throw error;
      });
  }, [setSession]);

  const resetPassword = useCallback((request: VerificationCodeRequest) => {
    // Sent as-typed (not lowercased) -- same reasoning as login: the backend needs the original
    // case to find a historical mixed-case Cognito account when no lowercase account exists yet.
    return axios
      .post<void>(
        "v1/auth/password-reset/request",
        { username: request.username.trim() },
        { baseURL: environment.newApiUrl }
      )
      .then(() => Promise.resolve())
      .catch(withServerErrorMessage);
  }, []);

  const verifyResetPassword = useCallback(
    async (request: VerifyResetRequest) => {
      await axios
        .post<void>(
          "v1/auth/password-reset/confirm",
          { username: request.username.trim(), verificationCode: request.verificationCode, password: request.password },
          { baseURL: environment.newApiUrl }
        )
        .catch(withServerErrorMessage);
      await login(request);
    },
    [login]
  );

  const verifySignUp = useCallback(
    async (request: VerifyRegisterRequest) => {
      await Auth.confirmSignUp(request.username.toLowerCase().trim(), request.verificationCode);
      await login(request);
    },
    [login]
  );

  const resendSignUpCode = useCallback((request: VerificationCodeRequest) => {
    return axios
      .post<void>(
        "v1/auth/resend-code",
        { username: request.username.trim() },
        { baseURL: environment.newApiUrl }
      )
      .then(() => Promise.resolve())
      .catch(withServerErrorMessage);
  }, []);

  /**
   * `reason` lets a caller that is signing the user out on their behalf (revoked access, failed
   * account load) leave a message behind for the login page. Called with no argument -- as the
   * sidebar's "Sign out" does -- it clears any stale message instead. The isAuthError guard keeps
   * a stray handler argument (e.g. onClick={signOut}) from being treated as a reason.
   */
  const signOut = useCallback((reason?: AuthError | null) => {
    setAuthError(isAuthError(reason) ? reason : null);
    return Promise.all([
      Auth.signOut({ global: true }).catch(() => null),
      axios.post("auth/logout", {}, { baseURL: environment.newApiUrl, withCredentials: true }).catch(() => null),
    ]).then(() => {
      triggerAppEvent(AppEvent.signOut);
      writeStoredSession(null);
      setSession(null);
      queryClient.clear();
      authChannel?.postMessage("signOut");
    });
  }, [setSession, queryClient]);

  // Cross-tab logout: a "remember"-mode session is shared across tabs via the cookie, so a
  // logout in one tab must clear the others too, or a "logged out" tab could keep showing
  // stale authenticated data until its next failed request. Also clears this tab's own
  // sessionStorage entry, if any, so a non-"remember" tab is signed out too when the user
  // explicitly logs out anywhere in the same browser.
  useEffect(() => {
    if (!authChannel) return;

    const onMessage = (event: MessageEvent) => {
      if (event.data === "signOut") {
        writeStoredSession(null);
        setSessionRef.current(null);
        queryClient.clear();
      }
    };

    authChannel.addEventListener("message", onMessage);
    return () => authChannel.removeEventListener("message", onMessage);
  }, [queryClient]);

  /**
   * Changes the signed-in "app" (password) user's own password. This app's sessions are
   * BE-issued (see `login`), not Amplify-managed, so there is no Amplify-cached user for
   * `Auth.changePassword` to operate on -- `Auth.currentAuthenticatedUser()` always rejects
   * locally before any request goes out. Instead this re-authenticates directly against Cognito
   * with the current password (same as `login` did before it moved server-side) to get a real
   * CognitoUser session, then changes the password on it.
   */
  const changePassword = useCallback(async (request: ChangePasswordRequest): Promise<void> => {
    const username = sessionRef.current?.username;
    if (!username) {
      return Promise.reject(errorMessages.auth.unauthorized);
    }

    const user = new CognitoUser({ Username: username, Pool: new CognitoUserPool({ UserPoolId: userPoolId, ClientId: defaultClientId }) });
    const authenticationDetails = new AuthenticationDetails({ Username: username, Password: request.password });

    await new Promise<void>((resolve, reject) => {
      user.authenticateUser(authenticationDetails, {
        onSuccess: () => resolve(),
        onFailure: (err) => reject(err),
        newPasswordRequired: () => reject(new Error("Your account requires a password change. Please sign in again.")),
      });
    });

    await new Promise<void>((resolve, reject) => {
      user.changePassword(request.password, request.newPassword, (err) => (err ? reject(err) : resolve()));
    });
  }, []);

  const contextValue: AuthContext = {
    session,
    setSession,
    isAuthReady,
    authError,
    clearAuthError,
    login,
    completeNewPassword,
    register,
    refresh,
    verifySignUp,
    resendSignUpCode,
    resetPassword,
    verifyResetPassword,
    signOut,
    changePassword,
  };

  return <Context.Provider value={{ ...contextValue }}>{props.children}</Context.Provider>;
}

function useAuthContext() {
  const context = useContext(Context);
  if (!context) {
    throw new Error(errorMessages.context.useHookWithinProvider);
  }
  return context;
}

export { AuthContextProvider, useAuthContext };